import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { toast } from "sonner";
import { SectionCard, EmptyState, useAccountingPermissions } from "./shared";
import { ACCOUNTING_BASE } from "./accountingNav";
import { formatBalance, formatCurrency, formatDateDMY } from "./reports/reportShared";

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => `${today().slice(0, 7)}-01`;

export default function MemberLedgerPage() {
  const navigate = useNavigate();
  const permissions = useAccountingPermissions();
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [membersLoading, setMembersLoading] = useState(true);
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/directory", { params: { activeOnly: true } });
        setMembers(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        toast.error(formatErr(err, "Could not load member directory"));
      } finally {
        setMembersLoading(false);
      }
    })();
  }, []);

  const load = async (member = selectedMember) => {
    setData(null);
    if (!member?.id) return;
    setSelectedMember(member);
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reports/receivables/statement", {
        params: { ownerId: member.id, wing: member.wing, flatNumber: member.flatNumber, ownerName: member.ownerName, fromDate, toDate },
      });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load this member ledger"));
    } finally {
      setLoading(false);
    }
  };

  const closeLedger = () => {
    setSelectedMember(null);
    setData(null);
  };

  const filteredMembers = members.filter((member) => {
    const needle = memberSearch.trim().toLowerCase();
    if (!needle) return true;
    return `${member.ownerName || ""} ${member.wing || ""} ${member.flatNumber || ""} ${member.ownerMobile || ""} ${member.tenantName || ""}`.toLowerCase().includes(needle);
  });

  const cancelReceipt = async (row) => {
    if (!window.confirm(`Cancel receipt ${row.voucherNumber}? A dated reversal will be posted; history will remain.`)) return;
    try {
      await api.post(`/society/accounting/engine/vouchers/${row.voucherId}/cancel`, {
        reason: "Cancelled from Member Ledger", reversalDate: today(),
      });
      toast.success("Receipt cancelled with an audit reversal");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not cancel this receipt"));
    }
  };

  const totals = (data?.rows || []).reduce((sum, row) => {
    sum.bill += Number(row.billAmount || 0);
    sum.receipt += row.entryType === "receipt" ? Number(row.credit || 0) : 0;
    sum.adjustment += Number(row.adjustmentAmount || (row.entryType === "adjustment" ? Number(row.debit || 0) - Number(row.credit || 0) : 0));
    sum.interest += Number(row.interestAmount || 0);
    return sum;
  }, { bill: 0, receipt: 0, adjustment: 0, interest: 0 });

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Member Ledger</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Date-wise accounting statement of bills, receipts, adjustments, interest and running outstanding.</p>
      </div>

      {!selectedMember ? (
        <SectionCard title="All members" subtitle={`${members.length} active member${members.length === 1 ? "" : "s"}`}>
          <div className="mb-4 max-w-md"><Input value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Search by member, flat, mobile or tenant..." /></div>
          {membersLoading ? <div className="py-8 text-center text-sm text-[#6B7280]">Loading members...</div> : filteredMembers.length === 0 ? <EmptyState>No members match this search.</EmptyState> : (
            <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
              <Table>
                <TableHeader><TableRow><TableHead>Flat</TableHead><TableHead>Member</TableHead><TableHead>Mobile</TableHead><TableHead>Occupancy</TableHead><TableHead>Tenant</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                <TableBody>{filteredMembers.map((member) => <TableRow key={member.id} className="cursor-pointer hover:bg-[#F9FAFB]" onClick={() => load(member)}><TableCell className="font-semibold">{[member.wing, member.flatNumber].filter(Boolean).join("-") || "—"}</TableCell><TableCell>{member.ownerName || "—"}</TableCell><TableCell>{member.ownerMobile || "—"}</TableCell><TableCell className="capitalize">{String(member.occupancyStatus || "—").replaceAll("_", " ")}</TableCell><TableCell>{member.tenantName || "—"}</TableCell><TableCell className="text-right"><button type="button" className="text-xs font-semibold text-[#111827] hover:underline" onClick={(e) => { e.stopPropagation(); load(member); }}>Open ledger</button></TableCell></TableRow>)}</TableBody>
              </Table>
            </div>
          )}
        </SectionCard>
      ) : loading ? <div className="text-sm text-[#6B7280]">Loading ledger...</div> : (
        <>
          <SectionCard title={`${selectedMember.ownerName || "Member"} · Flat ${[selectedMember.wing, selectedMember.flatNumber].filter(Boolean).join("-")}`} actions={<Button variant="outline" onClick={closeLedger}>Back to all members</Button>}>
            <div className="grid gap-4 md:grid-cols-[160px_160px_auto] md:items-end">
              <div><div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">From</div><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} /></div>
              <div><div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">To</div><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} /></div>
              <Button disabled={!fromDate || !toDate} onClick={() => load()}>Apply dates</Button>
            </div>
          </SectionCard>
          {!data ? <EmptyState>No ledger data is available for this member.</EmptyState> : <>
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[
              ["Opening", formatBalance(data.openingBalance)], ["Bills", formatCurrency(totals.bill || 0)],
              ["Receipts", formatCurrency(totals.receipt || 0)], ["Adjustments", formatCurrency(totals.adjustment || 0)],
              ["Interest", formatCurrency(totals.interest || 0)], ["Closing", formatBalance(data.closingBalance)],
            ].map(([label, value]) => <div key={label} className="rounded-sm border border-[#E5E7EB] bg-white p-3"><div className="text-[11px] uppercase tracking-wide text-[#6B7280]">{label}</div><div className="mt-1 font-bold text-[#111827]">{value}</div></div>)}
          </div>
          <SectionCard title={data.partyLabel} subtitle={`${data.fromDate} to ${data.toDate}`}>
            <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Voucher</TableHead><TableHead>Particulars</TableHead><TableHead>Reference</TableHead><TableHead className="text-right">Bill</TableHead><TableHead className="text-right">Adjustment</TableHead><TableHead className="text-right">Interest</TableHead><TableHead className="text-right">Received</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow className="bg-[#F9FAFB] font-semibold"><TableCell>{formatDateDMY(data.fromDate)}</TableCell><TableCell>Opening</TableCell><TableCell>—</TableCell><TableCell>Balance brought forward</TableCell><TableCell>—</TableCell><TableCell /><TableCell /><TableCell /><TableCell /><TableCell className="text-right">{formatBalance(data.openingBalance)}</TableCell><TableCell /></TableRow>
                  {data.rows.map((row, index) => (
                    <TableRow key={`${row.voucherId}-${index}`}>
                      <TableCell>{formatDateDMY(row.voucherDate)}</TableCell><TableCell className="capitalize">{row.entryType}</TableCell>
                      <TableCell><button className="font-semibold hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${row.voucherId}`)}>{row.voucherNumber || "View"}</button></TableCell>
                      <TableCell>{row.narration || "—"}</TableCell><TableCell>{row.referenceNo || "—"}</TableCell>
                      <TableCell className="text-right">{row.billAmount ? formatCurrency(row.billAmount) : "—"}</TableCell><TableCell className="text-right">{row.adjustmentAmount ? formatCurrency(row.adjustmentAmount) : row.entryType === "adjustment" ? formatCurrency(Number(row.debit || 0) - Number(row.credit || 0)) : "—"}</TableCell><TableCell className="text-right">{row.interestAmount ? formatCurrency(row.interestAmount) : "—"}</TableCell><TableCell className="text-right">{row.entryType === "receipt" && row.credit ? formatCurrency(row.credit) : "—"}</TableCell>
                      <TableCell className="text-right font-semibold">{formatBalance(row.runningBalance, row.runningSide)}</TableCell>
                      <TableCell className="text-right">{row.entryType === "receipt" && row.editableReceipt ? <div className="flex justify-end gap-2">{permissions.canEdit && <button className="text-xs font-semibold hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${row.voucherId}/edit`)}>Edit</button>}{permissions.has("accounting.approve") && <button className="text-xs font-semibold text-red-600 hover:underline" onClick={() => cancelReceipt(row)}>Cancel</button>}</div> : row.entryType === "receipt" ? <span className="text-xs text-[#6B7280]" title="Allocated receipts must be corrected through the linked bill">Allocated</span> : ""}</TableCell>
                    </TableRow>
                  ))}
                  {data.rows.length === 0 && <TableRow><TableCell colSpan={11} className="py-8 text-center text-[#6B7280]">No transactions in this date range.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </SectionCard>
          </>}
        </>
      )}
    </div>
  );
}
